import { useMemo } from 'react';
import { FS_NUVEMPAGO_ONBOARDING_HOME_CARD_VARIANT } from 'App/featuresFlags';
import {
  trackingKnowNuvemPagoClick,
  trackingNuvemPagoSettingsClick,
} from 'App/tracking';
import { useGetIsFreemium } from 'domains/Auth/hooks';
import useGetTags from 'domains/Auth/hooks/useGetTags';
import { useGetFeatureFlagVariantQuery } from 'domains/NuvemPagoCommons/nuvemPagoCommonsApi';
import {
  NUVEM_PAGO_ONBOARDING_ISOLATED_ROUTES,
  NUVEM_PAGO_AB_TEST_ROUTES,
  BR_ONBOARDING_VARIANTS,
} from '../constants';

export type VariantType = 'A' | 'B' | 'C';

export interface NuvemPagoOnboardingRules {
  variant: VariantType;
  showViewOtherMethodsButton: boolean;
  reviewUrl: string;
  viewOtherMethodsUrl: string;
  title: string;
  description: string;
  primaryButtonText: string;
  secondaryButtonText?: string;
  primaryButtonUrl: string;
  secondaryButtonUrl?: string;
  primaryButtonTracking?: () => void;
  secondaryButtonTracking?: () => void;
}

const VARIANT_A_CONFIG: NuvemPagoOnboardingRules = {
  variant: 'A',
  showViewOtherMethodsButton: true,
  reviewUrl: NUVEM_PAGO_AB_TEST_ROUTES.settings,
  viewOtherMethodsUrl: NUVEM_PAGO_AB_TEST_ROUTES.dashboard,
  title: 'onboarding.steps.nuvempago_commons.title',
  description: 'onboarding.steps.nuvempago_commons.description_variants',
  primaryButtonText: 'onboarding.steps.nuvempago_buttons.variant_a_primary',
  secondaryButtonText: 'onboarding.steps.nuvempago_buttons.variant_a_secondary',
  primaryButtonUrl: NUVEM_PAGO_AB_TEST_ROUTES.settings,
  secondaryButtonUrl: NUVEM_PAGO_AB_TEST_ROUTES.dashboard,
  primaryButtonTracking: () => trackingNuvemPagoSettingsClick('A'),
  secondaryButtonTracking: () => trackingKnowNuvemPagoClick('A'),
} as const;

const VARIANT_B_CONFIG: NuvemPagoOnboardingRules = {
  variant: 'B',
  showViewOtherMethodsButton: false,
  reviewUrl: NUVEM_PAGO_AB_TEST_ROUTES.dashboard,
  viewOtherMethodsUrl: '',
  title: 'onboarding.steps.nuvempago_commons.title_variants',
  description: 'onboarding.steps.nuvempago_commons.description_variants',
  primaryButtonText: 'onboarding.steps.nuvempago_buttons.variant_b_primary',
  primaryButtonUrl: NUVEM_PAGO_AB_TEST_ROUTES.dashboard,
  primaryButtonTracking: () => trackingKnowNuvemPagoClick('B'),
} as const;

const createVariantC = (isFreemium: boolean): NuvemPagoOnboardingRules => ({
  variant: 'C',
  showViewOtherMethodsButton: !isFreemium,
  reviewUrl: NUVEM_PAGO_AB_TEST_ROUTES.settings,
  viewOtherMethodsUrl: NUVEM_PAGO_ONBOARDING_ISOLATED_ROUTES.viewOtherMethods,
  title: 'onboarding.steps.nuvempago_commons.title',
  description: 'onboarding.steps.nuvempago_commons.description',
  primaryButtonText: 'onboarding.steps.nuvempago_buttons.freemium',
  secondaryButtonText: !isFreemium
    ? 'onboarding.steps.nuvempago_buttons.other'
    : undefined,
  primaryButtonUrl: NUVEM_PAGO_AB_TEST_ROUTES.settings,
  secondaryButtonUrl: !isFreemium
    ? NUVEM_PAGO_ONBOARDING_ISOLATED_ROUTES.viewOtherMethods
    : undefined,
});

export const useNuvemPagoOnboardingRules = (): NuvemPagoOnboardingRules => {
  const isFreemium = useGetIsFreemium();

  const { data: variantData } = useGetFeatureFlagVariantQuery(
    FS_NUVEMPAGO_ONBOARDING_HOME_CARD_VARIANT,
  );

  const tags = useGetTags();

  const hasBrOnboardingTag = BR_ONBOARDING_VARIANTS.some((tag) =>
    tags.includes(tag),
  );

  const rules = useMemo((): NuvemPagoOnboardingRules => {
    if (isFreemium && !variantData?.enabled && hasBrOnboardingTag) {
      return createVariantC(isFreemium);
    }

    const variantName = variantData?.name;

    switch (variantName) {
      case 'A':
        return VARIANT_A_CONFIG;
      case 'B':
        return VARIANT_B_CONFIG;
      default:
        return createVariantC(isFreemium);
    }
  }, [isFreemium, variantData, hasBrOnboardingTag]);

  return rules;
};
